import { useMemo, useState } from "react";
import { Box, Typography, Chip, Button, alpha, useTheme } from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import FindingCards from "./FindingCards";
import { evaluateFindings, buildRcaReport, tunedThresholds } from "./findings";
import { paramSection } from "./params";
import { checklistFor, inspectionSection } from "./inspection";

const usToClock = (us, baseUs) => {
  if (us == null || baseUs == null) return null;
  const total = Math.max(0, Math.round((us - baseUs) / 1e6));
  return `+${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * Rule output for a parsed log. Detection only — every entry states the number
 * that tripped it and the hardware to go check, so the conclusion stays the
 * reader's.
 */
const LogFindings = ({ log, showReport = true, paramAudit = null, inspectionRecord = null }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const findings = useMemo(() => evaluateFindings(log), [log]);
  // Which rules fired depends on these, and on a shared log the reader did not
  // set them — so say so here, not only in the exported report.
  const tuned = useMemo(() => tunedThresholds(log), [log]);

  // The report carries whatever the page has gathered: rules, the parameter
  // audit, and the inspection record as filled in so far.
  const report = () =>
    buildRcaReport(log, findings, {
      sections: [
        paramSection(paramAudit),
        inspectionRecord ? inspectionSection(checklistFor(findings), inspectionRecord) : "",
      ],
    });

  // Same base the timeline uses, so timestamps line up between the two panels.
  const baseUs = useMemo(() => {
    const s = log?.summary || {};
    const stamps = [...(s.modes || []), ...(s.errors || []), ...(s.events || [])]
      .map((e) => e.t_us)
      .filter((t) => t != null);
    return stamps.length ? Math.min(...stamps) : null;
  }, [log]);

  const downloadReport = () => {
    const md = report();
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(log?.file_name || "log").replace(/\.[^.]+$/, "")}-rca.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      downloadReport(); // clipboard blocked (insecure origin, permissions) — fall back to a file
    }
  };

  return (
    <Box>
      {tuned.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            backgroundColor: alpha(theme.palette.warning.main, 0.06),
          }}
        >
          <TuneOutlinedIcon sx={{ fontSize: 20, color: theme.palette.warning.main, mt: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              Thresholds tuned in this browser
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, color: theme.palette.text.secondary }}>
              These findings were judged against numbers that differ from the shipped defaults, so
              another reader of this log may see a different result.
            </Typography>
            <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {tuned.map((t) => (
                <Chip
                  key={t.key}
                  size="small"
                  label={`${t.label}: ${t.value}${t.unit ? ` ${t.unit}` : ""} (ships as ${t.shipped})`}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    color: theme.palette.warning.main,
                    backgroundColor: alpha(theme.palette.warning.main, 0.14),
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {findings.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            No rule fired. Nothing in the extracted summary crossed a threshold — which is not the same as a
            nominal flight.
          </Typography>
        </Box>
      ) : (
        <FindingCards findings={findings} clockFor={(f) => usToClock(f.at_us, baseUs)} />
      )}

      {showReport && log?.status === "ready" && (
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={copyReport}
            sx={{ textTransform: "none", color: theme.palette.text.secondary, borderColor: theme.palette.divider }}
          >
            {copied ? "Copied" : "Copy RCA draft"}
          </Button>
          <Button
            size="small"
            onClick={downloadReport}
            sx={{ textTransform: "none", color: theme.palette.text.secondary }}
          >
            Download .md
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LogFindings;
